import { Text as RNText, type TextProps as RNTextProps } from 'react-native';
import { StyleSheet, type UnistylesThemes } from 'react-native-unistyles';

import type { UnistylesStyleValue } from './UnistylesStyleValue';

type ThemeSpaceValue = Parameters<UnistylesThemes['dark']['space']>[0];
type SpaceValue = ThemeSpaceValue;
type FontWeight = keyof UnistylesThemes['dark']['fontWeight'];

type TextSize = keyof UnistylesThemes['dark']['fontSize'];
type TextType = 'itemTitle' | 'itemSubtitle' | 'itemSecondarySubtitle';
type TextVariant =
  | 'primary'
  | 'error'
  | 'success'
  | 'info'
  | 'warning'
  | 'secondary'
  | 'white'
  | 'black'
  | 'typography'
  | 'typographySecondary'
  | 'typographyMuted';

type TextOwnProps = {
  bold?: boolean;
  bottom?: UnistylesStyleValue<'bottom'>;
  center?: boolean;
  color?: Extract<UnistylesStyleValue<'color'>, string>;
  flexGrow?: UnistylesStyleValue<'flexGrow'>;
  flexShrink?: UnistylesStyleValue<'flexShrink'>;
  fontWeight?: FontWeight;
  italic?: boolean;
  left?: UnistylesStyleValue<'left'>;
  lineHeight?: TextSize | UnistylesStyleValue<'lineHeight'>;
  m?: SpaceValue;
  mb?: SpaceValue;
  ml?: SpaceValue;
  mr?: SpaceValue;
  mt?: SpaceValue;
  mx?: SpaceValue;
  my?: SpaceValue;
  maxWidth?: UnistylesStyleValue<'maxWidth'>;
  p?: SpaceValue;
  pb?: SpaceValue;
  pl?: SpaceValue;
  position?: UnistylesStyleValue<'position'>;
  pr?: SpaceValue;
  pt?: SpaceValue;
  px?: SpaceValue;
  py?: SpaceValue;
  right?: UnistylesStyleValue<'right'>;
  size?: TextSize;
  textAlign?: UnistylesStyleValue<'textAlign'>;
  top?: UnistylesStyleValue<'top'>;
  type?: TextType;
  variant?: TextVariant;
};

export interface TextProps
  extends Omit<RNTextProps, keyof TextOwnProps>,
    TextOwnProps {}

export default function Text({
  children,
  variant,
  type,
  size,
  bold,
  bottom,
  italic,
  center,
  color,
  flexGrow,
  flexShrink,
  fontWeight,
  left,
  lineHeight,
  m,
  mb,
  ml,
  mr,
  mt,
  mx,
  my,
  maxWidth,
  p,
  pb,
  pl,
  position,
  pr,
  pt,
  px,
  py,
  right,
  textAlign,
  top,
  style: extraStyles,
  ...rest
}: TextProps) {
  style.useVariants({
    size,
    variant,
    type,
    bold,
    italic,
    center,
  });

  return (
    <RNText
      style={[
        style.text({
          color,
          flexGrow,
          flexShrink,
          fontWeight,
          lineHeight,
          position,
          top,
          right,
          bottom,
          left,
          m,
          mb,
          ml,
          mr,
          mt,
          mx,
          my,
          maxWidth,
          p,
          pb,
          pl,
          pr,
          pt,
          px,
          py,
          textAlign,
        }),
        extraStyles,
      ]}
      {...rest}
    >
      {children}
    </RNText>
  );
}

type TextStyleOptions = Pick<
  TextOwnProps,
  | 'color'
  | 'bottom'
  | 'flexGrow'
  | 'flexShrink'
  | 'fontWeight'
  | 'left'
  | 'lineHeight'
  | 'm'
  | 'mb'
  | 'ml'
  | 'mr'
  | 'mt'
  | 'mx'
  | 'my'
  | 'maxWidth'
  | 'p'
  | 'pb'
  | 'pl'
  | 'position'
  | 'pr'
  | 'pt'
  | 'px'
  | 'py'
  | 'right'
  | 'textAlign'
  | 'top'
>;

const style = StyleSheet.create((theme) => ({
  text: ({
    bottom,
    color,
    flexGrow,
    flexShrink,
    fontWeight,
    left,
    lineHeight,
    m,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
    maxWidth,
    p,
    pb,
    pl,
    position,
    pr,
    pt,
    px,
    py,
    right,
    textAlign,
    top,
  }: TextStyleOptions) => ({
    color: color || theme.colors.typography,
    flexGrow,
    flexShrink,
    fontWeight: fontWeight ? theme.fontWeight[fontWeight] : undefined,
    lineHeight:
      typeof lineHeight === 'number'
        ? lineHeight
        : lineHeight
          ? theme.fontSize[lineHeight]
          : undefined,
    margin: m !== undefined ? theme.space(m) : undefined,
    marginBottom: mb !== undefined ? theme.space(mb) : undefined,
    marginHorizontal: mx !== undefined ? theme.space(mx) : undefined,
    marginLeft: ml !== undefined ? theme.space(ml) : undefined,
    marginRight: mr !== undefined ? theme.space(mr) : undefined,
    marginTop: mt !== undefined ? theme.space(mt) : undefined,
    marginVertical: my !== undefined ? theme.space(my) : undefined,
    maxWidth: typeof maxWidth === 'number' ? theme.size(maxWidth) : maxWidth,
    padding: p !== undefined ? theme.space(p) : undefined,
    paddingBottom: pb !== undefined ? theme.space(pb) : undefined,
    paddingHorizontal: px !== undefined ? theme.space(px) : undefined,
    paddingLeft: pl !== undefined ? theme.space(pl) : undefined,
    paddingRight: pr !== undefined ? theme.space(pr) : undefined,
    paddingTop: pt !== undefined ? theme.space(pt) : undefined,
    paddingVertical: py !== undefined ? theme.space(py) : undefined,
    position,
    top: typeof top === 'number' ? theme.space(top) : top,
    right: typeof right === 'number' ? theme.space(right) : right,
    bottom: typeof bottom === 'number' ? theme.space(bottom) : bottom,
    left: typeof left === 'number' ? theme.space(left) : left,
    textAlign,
    variants: {
      bold: {
        true: {
          fontWeight: theme.fontWeight.bold,
        },
      },
      italic: {
        true: {
          fontStyle: 'italic',
        },
      },
      center: {
        true: {
          textAlign: 'center',
        },
      },
      size: {
        '9xl': {
          fontSize: theme.fontSize['9xl'],
        },
        '8xl': {
          fontSize: theme.fontSize['8xl'],
        },
        '7xl': {
          fontSize: theme.fontSize['7xl'],
        },
        '6xl': {
          fontSize: theme.fontSize['6xl'],
        },
        '5xl': {
          fontSize: theme.fontSize['5xl'],
        },
        '4xl': {
          fontSize: theme.fontSize['4xl'],
        },
        '3xl': {
          fontSize: theme.fontSize['3xl'],
        },
        '2xl': {
          fontSize: theme.fontSize['2xl'],
        },
        '2xs': {
          fontSize: theme.fontSize['2xs'],
        },
        xl: {
          fontSize: theme.fontSize.xl,
        },
        lg: {
          fontSize: theme.fontSize.lg,
        },
        md: {
          fontSize: theme.fontSize.md,
        },
        sm: {
          fontSize: theme.fontSize.sm,
        },
        xs: {
          fontSize: theme.fontSize.xs,
        },
        default: {
          fontSize: theme.fontSize.lg,
        },
      },
      type: {
        itemTitle: {
          fontWeight: 600,
          fontSize: theme.fontSize.xl,
          color: theme.colors.typography,
          lineHeight: theme.fontSize.xl + 8,
        },
        itemSubtitle: {
          fontWeight: 500,
          fontSize: theme.fontSize.md,
          color: theme.colors.typographySecondary,
          lineHeight: theme.fontSize.md + 4,
        },
        itemSecondarySubtitle: {
          fontWeight: 500,
          fontSize: theme.fontSize.xs,
          color: theme.colors.typographySecondary,
          lineHeight: theme.fontSize.xs + 4,
        },
      },
      variant: {
        primary: {
          color: theme.colors.primary,
        },
        error: {
          color: theme.colors.error,
        },
        success: {
          color: theme.colors.success,
        },
        info: {
          color: theme.colors.info,
        },
        warning: {
          color: theme.colors.warning,
        },
        secondary: {
          color: theme.colors.secondary,
        },
        white: {
          color: theme.colors.white,
        },
        black: {
          color: theme.colors.black,
        },
        typography: {
          color: theme.colors.typography,
        },
        typographySecondary: {
          color: theme.colors.typographySecondary,
        },
        typographyMuted: {
          color: theme.colors.typographyMuted,
        },
      },
    },
  }),
}));
